
import Vue from 'vue';
import { Component, Emit, Prop, Ref, Watch } from 'vue-property-decorator';
import { Route } from 'vue-router';
import { namespace } from 'vuex-class';

import InfiniteScroll from '@/components/list/InfiniteScroll.vue';
import PosterREST from '@/components/poster/REST/PosterREST.vue';
import GridVirtualScroller from '@/components/virtual-scroller/GridVirtualScroller.vue';

import { PopularTitle, Title } from '@/interfaces/titles';

const Routing = namespace('routing');
const User = namespace('user');

@Component({
	name: 'TitleListGridREST',
	components: { GridVirtualScroller, PosterREST, InfiniteScroll },
})
export default class TitleListGridREST extends Vue {
	@Prop() titles: PopularTitle[];
	@Prop() isAtEndOfList: boolean;
	@Prop() isFetching: boolean;
	@Prop() isFirstPage: boolean;
	@Prop() loading: boolean;
	@Prop() hasNoTitles: boolean;
	@Prop({ default: true }) virtualScrolling: boolean;
	@Prop({ type: Boolean }) isOutdated: boolean;

	@Ref('infiniteScroll') readonly infiniteScroll?: InfiniteScroll;
	@Ref('gridVirtualScroller') gridVirtualScroller?: GridVirtualScroller<PopularTitle>;

	@Routing.State activeRoute: Route;

	@User.Getter isLoggedIn: (providerName?: string) => boolean;
	@User.Getter isHideSeenDislikedTitlesEnabled: boolean;
	@User.Getter isPremium: boolean;

	paginationToken = '';
	isMounted = false;
	gridHasRows = false;

	@Watch('titles')
	onTitlesChange() {
		this.gridVirtualScroller?.updateRows();
	}

	@Emit('onPosterClick')
	onPosterClick(title: Title, index: number) {
		return { title, index };
	}

	get disableInfiniteScroll() {
		const isTitleDetailPage = this.activeRoute.name?.includes('app.detail');
		return !this.gridHasRows || this.hasNoTitles || this.isAtEndOfList || this.isFetching || isTitleDetailPage;
	}

	// CLS issue: upon filtering, the posters may change their position e.g. from 1st to 3rd.
	// this change of order, together with a not-perfect mobile connection that will take more than 500ms for the filtering request, will be picked up as CLS: the existing element changes its position.
	// to circumvent this, we give the posters a :key that is sort of bound to the request, so we can re-create the posters instead of being reused and moved through the DOM.
	get titlesHash() {
		const titles = this.titlesToDisplay.slice(0, this.IMG_POSTER_LOAD_COUNT) as PopularTitle[]; // TODO Not clear that's the proper type.

		return titles.map(title => (title as PopularTitle).jw_entity_id).join('-');
	}

	get IMG_POSTER_LOAD_COUNT() {
		return global.IMG_POSTER_LOAD_COUNT;
	}

	get titlesToDisplay() {
		if (this.isHideSeenDislikedTitlesEnabled) {
			return this.titles.filter(title => !title.hideTitle);
		}

		return this.titles;
	}

	get lastPaginationToken(): string {
		if (this.titles.length) {
			const [lastItem] = this.titles.slice(-1);
			return lastItem.pagination_token;
		}
		return '';
	}

	get shouldShowQuickActions() {
		return this.isLoggedIn();
	}

	get shouldRender() {
		return BUILD_CONFIG.globals.BUILD_TARGET === 'SPA' || this.isMounted;
	}

	mounted() {
		this.isMounted = true;
		if (this.titles) {
			this.paginationToken = this.lastPaginationToken;
		}
	}

	// Resets the internal state of the infinite scroll. Needs to be called at each filter change.
	resetInfiniteScroll() {
		this.infiniteScroll?.reset();
		this.gridVirtualScroller?.resetRows();
	}
}
